import { useEffect } from 'react'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { BirdieLiveBoardView } from 'src/components/BirdieBoard/birdieFeedVerdict'
import { IDLE_LIVE_BOARD } from 'src/components/BirdieBoard/birdieFeedVerdict'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

/**
 * B41 — what the Birdie board is CURRENTLY DRAWING, for the surfaces that must
 * never get ahead of it.
 *
 * The social feed settles ball by ball, and the one thing it may not do is show
 * a verdict before the viewer's own screen has shown the putt behind it. There
 * were two ways to guarantee that: recompute the hold in the feed, or read the
 * board's own output. Recomputing means a second copy of the spoiler rules,
 * a second clock and a second settle-window read — three chances to drift, on
 * the side where drifting leaks the round.
 *
 * So the board publishes, and everything else reads. One writer, and a reader
 * that cannot lead the picture because it IS the picture.
 *
 * Deliberately NOT part of `birdiePutts`: that slice is the raw socket input,
 * held and unheld together. This one is the held, dressed output. Keeping them
 * apart is what stops a future reader reaching for the wrong one.
 */
export interface BirdieLiveBoardState {
  /** Stable across an unchanged publish — see `publish`. */
  view: BirdieLiveBoardView
}

export interface BirdieLiveBoardActions {
  /** Called by the board with what it is drawing. Cheap when nothing moved. */
  publish: (next: BirdieLiveBoardView) => void
  /** Back to idle. The board clears on unmount so a stale round cannot linger. */
  clear: () => void
}

type BirdieLiveBoardStore = BirdieLiveBoardState & { actions: BirdieLiveBoardActions }

/**
 * Whether two published views are the same picture.
 *
 * Marks are compared by VALUE. The board rebuilds the array whenever its phase
 * recomputes — which is every tick — so comparing by reference would replace
 * the stored object several times a second and re-render every feed row with
 * it. That is the `useSyncExternalStore` trap this codebase has already paid
 * for once, and the reason the store hands back one stable object.
 */
function samePicture(a: BirdieLiveBoardView, b: BirdieLiveBoardView): boolean {
  if (a.raceId !== b.raceId) return false
  if (a.revealedType !== b.revealedType) return false
  if (a.marks.length !== b.marks.length) return false
  for (let i = 0; i < a.marks.length; i++) {
    if (a.marks[i] !== b.marks[i]) return false
  }
  return true
}

/**
 * The reveal is held behind the putts it rides on, so a view with nothing on
 * screen carries no type. Normalised HERE rather than at each reader: a rule
 * that keeps a spoiler in is worth exactly one copy.
 */
function normalise(next: BirdieLiveBoardView): BirdieLiveBoardView {
  const marks = next.marks.slice()
  if (marks.length === 0) {
    return next.raceId === undefined ? { marks } : { raceId: next.raceId, marks }
  }
  return {
    ...(next.raceId === undefined ? {} : { raceId: next.raceId }),
    marks,
    ...(next.revealedType === undefined ? {} : { revealedType: next.revealedType })
  }
}

export const useBirdieLiveBoardStore = create<BirdieLiveBoardStore>()(
  devtools(
    (set, get) => ({
      view: IDLE_LIVE_BOARD,
      actions: {
        publish: (next: BirdieLiveBoardView) => {
          const normalised = normalise(next)
          const current = get().view
          // A ROUND ONLY EVER GAINS PUTTS. The board empties its own marks once
          // the settlement is on screen, because its tiles hand over to the
          // settled classes at that point — but the feed still needs the
          // round's answer, or every decided row would fall back to the generic
          // "resolved means lost" rule for the length of the payout window.
          //
          // Keeping them cannot leak anything: the settlement is already on
          // screen. The round's own end is what resets this — a new race id
          // below, or `clear` on unmount.
          if (
            normalised.marks.length === 0 &&
            current.marks.length > 0 &&
            normalised.raceId === current.raceId
          ) {
            return
          }
          if (samePicture(current, normalised)) return
          set({ view: normalised }, undefined, 'publish')
        },
        clear: () => {
          if (samePicture(get().view, IDLE_LIVE_BOARD)) return
          set({ view: IDLE_LIVE_BOARD }, undefined, 'clear')
        }
      }
    }),
    { name: 'BirdieLiveBoardStore', enabled: isDevtoolsEnabled() }
  )
)

export const useBirdieLiveBoardActions = (): BirdieLiveBoardActions =>
  useBirdieLiveBoardStore((state) => state.actions)

/**
 * The board's end of the contract: publish what is being drawn, and let go on
 * unmount so a finished round cannot keep dressing the feed.
 *
 * A hook rather than two effects inlined in `BirdieBoard` because this IS the
 * spoiler guard — the feed is only safe while the value published here is the
 * board's own held output — and a guard that cannot be tested on its own tends
 * not to stay one.
 */
export const useBirdiePublishLiveBoard = (view: BirdieLiveBoardView): void => {
  const actions = useBirdieLiveBoardActions()
  const { raceId, marks, revealedType } = view
  useEffect(() => {
    actions.publish({
      ...(raceId === undefined ? {} : { raceId }),
      marks,
      ...(revealedType === undefined ? {} : { revealedType })
    })
  }, [actions, marks, raceId, revealedType])
  useEffect(
    () => () => {
      actions.clear()
    },
    [actions]
  )
}

/**
 * What the board is drawing. A stable reference while the picture is unchanged,
 * so a consumer may use it directly in a dependency array.
 */
export const useBirdieLiveBoard = (): BirdieLiveBoardView =>
  useBirdieLiveBoardStore((state) => state.view)
